import { useMemo, useState } from "react";
import styles from "./index.module.css";
import Loader from "shared/Loader";
import Link from "shared/Link";

export default function TicketIssues({
  issues = [],
  isLoading,
  isRefetching,
  error,
  resolveTicketIssue,
  refetch,
  iboSearch,
}) {
  const [submittingAction, setSubmittingAction] = useState(null);

  const normalizedIssues = useMemo(() => {
    const normalizedSearch = String(iboSearch || "").trim();

    return (issues || [])
      .map((issue) => ({
        ...issue,
      }))
      .filter((issue) => {
        if (!normalizedSearch) return true;
        return String(issue.ibo || "").includes(normalizedSearch);
      });
  }, [issues, iboSearch]);

  const handleResolve = async (issue, actionCode, iboNumber) => {
    if (!actionCode || submittingAction) return;

    setSubmittingAction({
      attendeeTicketId: issue.attendeeTicketId,
      actionCode,
    });

    try {
      await resolveTicketIssue.mutateAsync({
        attendeeTicketId: issue.attendeeTicketId,
        actionCode,
        iboNumber,
      });
    } finally {
      setSubmittingAction(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.stateMessageError}>
        {error.message || "Failed to load ticket issues"}
      </div>
    );
  }

  if (!normalizedIssues.length) {
    return <div className={styles.stateMessage}>No ticket issues found.</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <div className={styles.infoItem}>
          <div className={styles.infoValue}>
            Total: {normalizedIssues.length}
          </div>
        </div>

        <div className={`${styles.infoItem} ${styles.refreshItem}`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={refetch}
            disabled={resolveTicketIssue.isLoading}
          >
            {isRefetching ? <Loader size={20} color="#ffffff" /> : "Refresh"}
          </button>
        </div>
      </div>

      <div className={styles.dividerWrapper}>
        <div className={styles.divider} />
      </div>

      <div className={`${styles.headerRow} ${styles.ticketIssuesHeaderRow}`}>
        <div>Ticket</div>
        <div>Status </div>
        <div>IBO / Name</div>
        <div>Attendee</div>
        <div>Event</div>
        <div>Ticket</div>
      </div>

      <div className={styles.issueList}>
        {normalizedIssues.map((issue) => {
          const isSubmittingCurrentAction =
            submittingAction?.attendeeTicketId === issue.attendeeTicketId;

          const hasAction1 = Number(issue.action1Code) > 0 && issue.action1Text;
          const hasAction2 = Number(issue.action2Code) > 0 && issue.action2;

          return (
            <div key={issue.attendeeTicketId} className={styles.issueCard}>
              <div className={`${styles.issueRow} ${styles.ticketIssuesRow}`}>
                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    {issue.attendeeTicketId}
                  </div>
                  <div className={styles.secondaryValue}>
                    {issue.ticketCreatedOn || "--"}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.statusValue}>
                    {issue.ticketStatus || "--"}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>{issue.name}</div>
                  <div className={styles.secondaryValue}>
                    <Link
                      to={`/events/orders/ibo/${issue.ibo}`}
                      onClick={() => {}}
                    >
                      IBO: {issue.ibo}
                    </Link>
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    {issue.attendee || "--"}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    {issue.wwgEvent || "--"}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    {issue.ticket || "--"}
                  </div>
                </div>

                <div className={styles.issueActions}>
                  {hasAction1 && (
                    <button
                      type="button"
                      className={styles.actionPrimary}
                      disabled={Boolean(submittingAction)}
                      onClick={() =>
                        handleResolve(issue, issue.action1Code, issue.ibo)
                      }
                    >
                      {isSubmittingCurrentAction &&
                      submittingAction?.actionCode === issue.action1Code ? (
                        <Loader size={20} color="#ffffff" />
                      ) : (
                        issue.action1Text
                      )}
                    </button>
                  )}

                  {hasAction2 && (
                    <button
                      type="button"
                      className={styles.actionSecondary}
                      disabled={Boolean(submittingAction)}
                      onClick={() =>
                        handleResolve(issue, issue.action2Code, issue.ibo)
                      }
                    >
                      {isSubmittingCurrentAction &&
                      submittingAction?.actionCode === issue.action2Code ? (
                        <Loader size={20} color="#ffffff" />
                      ) : (
                        issue.action2
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
